import { Block } from '@basehub/data/replicache/index'
import { TreeManager, TreeNode } from '@basehub/utils/tree/index'
import { getBlockTitleForDisplay } from '~/lib/get-block-title-for-display'

interface TransformRecentNodesOptions {
  nodes: Array<{
    id: string
    lastOpened?: number
    openCount?: number
  }>
  getNode?: TreeManager<Block, NonNullable<unknown>>['getNode']
  max?: number
  filter?: (block: Block) => boolean
  breadcrumbsDepth?: number
}

interface TransformedNode {
  label: string
  value: string
  breadcrumbs: string[]
  node?: Block
  lastOpened?: number
  openCount?: number
  type?: Block['type']
}

export const getNodeBreadcrumbs = (
  node: TreeNode<Block>,
  getNode: TreeManager<Block, NonNullable<unknown>>['getNode'],
  depth: number = 2
): string[] => {
  if (!node.parentKey || !getNode) return []

  const breadcrumbs: string[] = []
  let current = node
  let count = 0

  while (current.parentKey && count < depth) {
    const parent = getNode(current.parentKey)
    if (!parent || parent.value.type === 'root') break

    const title = getBlockTitleForDisplay(parent.value.title)
    if (title) {
      breadcrumbs.unshift(title)
      count++
    }

    current = parent
  }

  return breadcrumbs.length > 0 ? breadcrumbs : ['/']
}

export const transformRecentNodes = ({
  nodes,
  getNode,
  max = 5,
  filter,
  breadcrumbsDepth = 2,
}: TransformRecentNodesOptions): TransformedNode[] => {
  if (!nodes.length || !getNode) return []

  const result: TransformedNode[] = []

  for (const recentNode of nodes) {
    if (result.length >= max) break

    const node = getNode(recentNode.id)
    if (!node?.value) continue

    const block = node.value
    if (filter && !filter(block)) continue

    result.push({
      label: getBlockTitleForDisplay(block.title),
      value: block.id,
      breadcrumbs: getNodeBreadcrumbs(node, getNode, breadcrumbsDepth),
      node: block,
      type: block.type,
      lastOpened: recentNode.lastOpened,
      openCount: recentNode.openCount,
    } satisfies TransformedNode)
  }

  return result
}
